// Student Growth Mission: tiles; full-screen interview iframe; JWT via postMessage (not query string).
// Mistake: JWT in URL leaked in logs; switched to postMessage—child must verify event.origin.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getAccessToken } from "helpers/Auth.helper";

/** Must match handler in REACT_APP_C_IDOL_TALK_URL app (check event.origin before trusting). */
const WANNABES_STUDENT_JWT_MESSAGE_TYPE = "WANNABES_STUDENT_JWT" as const;

const INTERVIEW_IFRAME_BASE =
  (process.env.REACT_APP_C_IDOL_TALK_URL || "").trim() ||
  "https://idol-talk.wannabes.app";

const shouldEnableIdolTalkDebugJwt = (): boolean => {
  const env = (process.env.REACT_APP_ENV || "").trim().toLowerCase();
  return env === "local" || env === "dev" || env === "stage";
};

const getInterviewIframeSrc = (): string => {
  try {
    const u = new URL(INTERVIEW_IFRAME_BASE);
    u.searchParams.delete("jwt");
    if (shouldEnableIdolTalkDebugJwt()) {
      u.searchParams.set("debugJwt", "1");
    } else {
      u.searchParams.delete("debugJwt");
    }
    return u.toString();
  } catch {
    return INTERVIEW_IFRAME_BASE;
  }
};

const getInterviewIframeTargetOrigin = (): string | null => {
  try {
    return new URL(INTERVIEW_IFRAME_BASE).origin;
  } catch {
    return null;
  }
};

const GrowthMission = () => {
  const [isInterviewOpen, setIsInterviewOpen] = useState(false);
  const interviewIframeRef = useRef<HTMLIFrameElement>(null);

  const sendJwtToInterviewIframe = useCallback(() => {
    const targetOrigin = getInterviewIframeTargetOrigin();
    const jwt = getAccessToken();
    const win = interviewIframeRef.current?.contentWindow;
    if (!win || !targetOrigin || !jwt) {
      return;
    }
    const payload = { type: WANNABES_STUDENT_JWT_MESSAGE_TYPE, jwt };
    win.postMessage(payload, targetOrigin);
    window.setTimeout(() => {
      interviewIframeRef.current?.contentWindow?.postMessage(payload, targetOrigin);
    }, 400);
  }, []);

  useEffect(() => {
    if (!isInterviewOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsInterviewOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isInterviewOpen]);

  useEffect(() => {
    if (!isInterviewOpen) {
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isInterviewOpen]);

  return (
    <main className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            {getLanguageValue(PAGE_TYPE.STUDENT, "growthMission.txtTitle") || "미션"}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {getLanguageValue(PAGE_TYPE.STUDENT, "growthMission.txtDesc") ||
              "오늘의 트레이닝 미션을 선택해 진행해보세요"}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            type="button"
            className="w-full bg-white border border-gray-200 rounded-2xl text-left shadow-sm hover:shadow-md hover:border-gray-300 transition-all overflow-hidden"
          >
            <div className="flex items-center">
              <img
                src="/imgs/student/img-mission-vocal.png"
                alt=""
                className="w-14 h-14 object-cover flex-shrink-0"
                loading="lazy"
              />
              <div className="px-5 py-4">
                <div className="text-base font-semibold text-gray-900">보컬 트레이닝</div>
              </div>
            </div>
          </button>

          <button
            type="button"
            className="w-full bg-white border border-gray-200 rounded-2xl text-left shadow-sm hover:shadow-md hover:border-gray-300 transition-all overflow-hidden"
          >
            <div className="flex items-center">
              <img
                src="/imgs/student/img-mission-dance.png"
                alt=""
                className="w-14 h-14 object-cover flex-shrink-0"
                loading="lazy"
              />
              <div className="px-5 py-4">
                <div className="text-base font-semibold text-gray-900">댄스 트레이닝</div>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setIsInterviewOpen(true)}
            className="w-full bg-white border border-gray-200 rounded-2xl text-left shadow-sm hover:shadow-md hover:border-gray-300 transition-all overflow-hidden"
          >
            <div className="flex items-center">
              <img
                src="/imgs/student/img-mission-interview.png"
                alt=""
                className="w-14 h-14 object-cover flex-shrink-0"
                loading="lazy"
              />
              <div className="px-5 py-4">
                <div className="text-base font-semibold text-gray-900">인터뷰</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {isInterviewOpen && (
        <div
          className="fixed inset-0 z-[200] bg-white"
          role="dialog"
          aria-modal="true"
          aria-label={getLanguageValue(PAGE_TYPE.STUDENT, "growthMission.txtInterviewFrameTitle") || "인터뷰"}
        >
          <iframe
            ref={interviewIframeRef}
            src={getInterviewIframeSrc()}
            title={getLanguageValue(PAGE_TYPE.STUDENT, "growthMission.txtInterviewFrameTitle") || "Idol Talk"}
            className="fixed inset-0 z-[200] h-full w-full border-0"
            allow="camera; microphone; clipboard-read; clipboard-write; fullscreen; web-share"
            onLoad={sendJwtToInterviewIframe}
          />
          <div className="fixed top-0 left-0 right-0 z-[201] flex h-12 items-center justify-end px-2 pointer-events-none">
            <button
              type="button"
              onClick={() => setIsInterviewOpen(false)}
              className="pointer-events-auto rounded-full border border-gray-200/80 bg-white/90 p-2 text-gray-700 shadow-sm backdrop-blur-sm hover:bg-white"
              aria-label={getLanguageValue(PAGE_TYPE.STUDENT, "growthMission.txtClose")}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default GrowthMission;

